import {
  ExpenseCustomFieldFilterInput,
  ExpensePaymentMethod,
  ExpensePolicyStatus,
  ExpenseReportAnalyticGroup,
  ExpenseReportStatus,
  ExpenseStatus,
  GraphAnalyticInterval,
  StipendExpenseAnalyticGroup,
  StipendExpenseStatus,
  useAdminReportGraphMetricsLazyQuery,
  useAdminStipendGraphMetricsLazyQuery,
} from "@common/utils/graphql";
import { useEffect, useMemo } from "react";
import { X_AXIS_LABEL_KEY } from "./constants";
import { ChartConfig } from "@/components/ui/chart";
import { formatDateForInterval, getBarColorFor } from "./utils";
import { addTimezoneOffsetToUTCZeroTimeIfNeeded } from "@common/components/utils";

// Types
type Base = {
  startDate: Date;
  endDate: Date;
  interval: GraphAnalyticInterval;
  employeeId?: string[];
  merchantName?: string[];
  departmentId?: number[];
  reviewerEmployeeId?: string[];
  paymentMethod?: ExpensePaymentMethod[];
};

export type ReportQuery = Base & {
  queryType: "report";
  reportProps: {
    groupedBy: ExpenseReportAnalyticGroup;
    expenseCategoryGLId?: number[];
    expenseReportStatus?: ExpenseReportStatus[];
    expenseStatus?: ExpenseStatus[];
    policyStatus?: ExpensePolicyStatus[];
    customField?: ExpenseCustomFieldFilterInput[];
    groupByCustomFieldEntityId?: number | null;
  };
  stipendProps?: undefined;
};

export type StipendQuery = Base & {
  queryType: "stipend";
  reportProps?: undefined;
  stipendProps: {
    groupedBy: StipendExpenseAnalyticGroup;
    stipendExpenseStatus?: StipendExpenseStatus[];
    stipendRuleId?: number[];
  };
};

export type ChartQuery = ReportQuery | StipendQuery;

// Component
export const useChartQuery = ({
  queryType,
  startDate,
  endDate,
  departmentId,
  employeeId,
  merchantName,
  reviewerEmployeeId,
  reportProps,
  stipendProps,
  interval,
  paymentMethod,
}: ChartQuery) => {
  const [
    adminReportGraphMetricsQuery,
    { data: rawReportData, loading: reportLoading },
  ] = useAdminReportGraphMetricsLazyQuery();
  const [
    adminStipendGraphMetricsQuery,
    { data: rawStipendData, loading: stipendLoading },
  ] = useAdminStipendGraphMetricsLazyQuery();

  const loading = queryType === "report" ? reportLoading : stipendLoading;

  useEffect(() => {
    const endDateStr = endDate.toISOString().split("T")[0] + "T23:59:59.999Z";
    const startDateStr =
      startDate.toISOString().split("T")[0] + "T00:00:00.000Z";

    if (queryType === "report") {
      adminReportGraphMetricsQuery({
        variables: {
          filters: {
            transactedBefore: endDateStr,
            transactedAfter: startDateStr,
            departmentId,
            employeeId,
            merchantName,
            reviewerEmployeeId,
            paymentMethod,
            expenseCategoryGLId: reportProps.expenseCategoryGLId,
            expenseReportStatus: reportProps.expenseReportStatus,
            expenseStatus: reportProps.expenseStatus,
            policyStatus: reportProps.policyStatus,
            customField: reportProps.customField,
          },
          groupedBy: reportProps.groupedBy,
          groupByCustomFieldEntityId: reportProps.groupByCustomFieldEntityId,
          interval,
        },
      });
    } else {
      adminStipendGraphMetricsQuery({
        variables: {
          filters: {
            transactedBefore: endDateStr,
            transactedAfter: startDateStr,
            departmentId,
            employeeId,
            merchantName,
            reviewerEmployeeId,
            paymentMethod,
            stipendExpenseStatus: stipendProps.stipendExpenseStatus,
            stipendRuleId: stipendProps.stipendRuleId,
            // TODO: add policy status for stipends
          },
          groupedBy: stipendProps.groupedBy,
          interval,
        },
      });
    }
  }, [
    interval,
    queryType,
    reportProps,
    endDate,
    startDate,
    merchantName,
    adminReportGraphMetricsQuery,
    adminStipendGraphMetricsQuery,
    stipendProps,
    departmentId,
    employeeId,
    reviewerEmployeeId,
    paymentMethod,
  ]);

  // TODO: @rushil these both return type GraphAnalyticMetric so it causes caching issues
  const data = useMemo(() => {
    if (queryType === "report") {
      return rawReportData?.adminReportGraphMetrics.__typename ===
        "QueryAdminReportGraphMetricsSuccess"
        ? rawReportData.adminReportGraphMetrics.data
        : undefined;
    } else {
      return rawStipendData?.adminStipendGraphMetrics?.__typename ===
        "QueryAdminStipendGraphMetricsSuccess"
        ? rawStipendData.adminStipendGraphMetrics.data
        : undefined;
    }
  }, [queryType, rawReportData, rawStipendData]);

  const chartConfig = useMemo(() => {
    if (!data) return undefined;

    const metrics = [...data.metrics];
    // Generate a color for each metric and save it to the config
    const value: ChartConfig = {};

    // Add a "Other" option to the config if more than 5 metrics
    if (metrics.length > 5) {
      value["Other"] = {
        color: "#676767",
      };
    }

    // Sort metrics alphabetically by name
    // Then add them to the config
    metrics
      .sort((a, b) => b.groupedEntityName.localeCompare(a.groupedEntityName))
      .forEach(({ groupedEntityName }) => {
        value[groupedEntityName] = {
          color: getBarColorFor(groupedEntityName),
        };
      });

    return value;
  }, [data]);

  const chartData = useMemo(() => {
    if (!data) return undefined;

    // Store a map of date to values for each metric
    const valuesMap: Record<string, Record<string, number>> = {};
    data.metrics.forEach((metric) => {
      metric.values.forEach(({ date, value }) => {
        if (!valuesMap[date]) {
          valuesMap[date] = {};
        }
        valuesMap[date][metric.groupedEntityName] = value;
      });
    });

    // Convert the map to an array of values as expected by Chart component
    const values = Object.entries(valuesMap)
      .sort((a, b) => {
        return new Date(a[0]).getTime() - new Date(b[0]).getTime();
      })
      .map(([date, values]) => {
        // If values is longer than 5, merge all but the first 5 into "Other"
        const entries = Object.entries(values);
        const sortedEntries = entries.sort((a, b) => b[1] - a[1]);
        const top5Entries = sortedEntries.slice(0, 5);
        const otherValue = sortedEntries
          .slice(3)
          .reduce((sum, [, value]) => sum + value, 0);
        const result = Object.fromEntries(top5Entries);
        if (otherValue > 0) {
          result["Other"] = otherValue;
        }

        // Make sure to adjust date to UTC zero time
        const dateObj = new Date(date);
        const adjustedDate = addTimezoneOffsetToUTCZeroTimeIfNeeded(dateObj);
        const xAxisLabel = formatDateForInterval(adjustedDate, interval);

        return {
          [X_AXIS_LABEL_KEY]: xAxisLabel,
          ...result,
        };
      });
    return values;
  }, [data, interval]);

  return { config: chartConfig, data: chartData, loading };
};
